import { motion } from 'framer-motion';
import { Lock, X, Mail, Phone, CheckCircle2 } from 'lucide-react';
import { useProfile } from '../../../../../context/profile/ProfileContext';

const AccountSecuritySection = () => {
  const {
    formData,
    isLoading,
    renderInput,
    showForgotPassword,
    setShowForgotPassword,
    otpMethod,
    otp,
    otpSent,
    setOtpSent,
    otpVerified,
    handleSendOtp,
    handleOtpChange,
    handleOtpPaste,
    handleVerifyOtp,
    handleResetPasswordFinal,
    handleUpdatePasswordDirect
  } = useProfile();

  return (
    <div className="pt-2">
      <div className="border-b border-slate-100 pb-2.5 mb-4">
        <h2 className="font-semibold text-slate-900 text-base">Change password</h2>
        <p className="text-xs text-slate-500 mt-0.5 font-normal">Update your account security and password</p>
      </div>

      {!showForgotPassword ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Lock size={15} className="text-[#067978]" />
              <span className="text-xs font-semibold text-slate-800">
                {formData.hasPassword ? 'Change password' : 'Set your password'}
              </span>
            </div>
            {formData.hasPassword && (
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-xs font-semibold text-[#067978] hover:underline cursor-pointer"
              >
                Forgot password?
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {formData.hasPassword && renderInput("Current Password", "currentPassword", formData.currentPassword, "password", false, Lock, false, true)}
            {renderInput(formData.hasPassword ? "New Password" : "Set Password", "newPassword", formData.newPassword, "password", false, Lock, false, true)}
          </div>

          <div className="flex justify-end pt-1">
            <button
              type="button"
              onClick={handleUpdatePasswordDirect}
              disabled={isLoading || !formData.newPassword}
              className="px-5 py-2.5 bg-[#067978] hover:bg-[#056362] text-white rounded-xl font-semibold text-xs transition-all shadow-xs cursor-pointer disabled:opacity-50"
            >
              {isLoading ? 'Updating Password...' : 'Update Password'}
            </button>
          </div>
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-slate-900 p-6 rounded-2xl text-white space-y-6 shadow-xl"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/10 rounded-xl"><Lock size={18} className="text-[#0ea5a0]" /></div>
              <h4 className="font-bold text-base">OTP Verification</h4>
            </div>
            <button onClick={() => setShowForgotPassword(false)} className="p-2 hover:bg-white/10 rounded-full transition-colors cursor-pointer">
              <X size={18} />
            </button>
          </div>

          {!otpSent ? (
            <div className="space-y-6">
              <p className="text-xs text-slate-300 leading-relaxed font-normal">
                Verify your identity to reset your password. Choose your preferred method.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => handleSendOtp('email')}
                  disabled={isLoading}
                  className="bg-white/5 hover:bg-white/10 border border-white/10 p-4 rounded-xl transition-all text-center group cursor-pointer"
                >
                  <Mail className="mx-auto mb-2 text-slate-400 group-hover:text-[#0ea5a0]" size={20} />
                  <span className="block text-xs font-bold">
                    {isLoading && otpMethod === 'email' ? 'Sending...' : 'Email OTP'}
                  </span>
                </button>
                <button
                  onClick={() => handleSendOtp('sms')}
                  disabled={isLoading}
                  className="bg-white/5 hover:bg-white/10 border border-white/10 p-4 rounded-xl transition-all text-center group cursor-pointer"
                >
                  <Phone className="mx-auto mb-2 text-slate-400 group-hover:text-[#0ea5a0]" size={20} />
                  <span className="block text-xs font-bold">
                    {isLoading && otpMethod === 'sms' ? 'Sending...' : 'Mobile OTP'}
                  </span>
                </button>
              </div>
            </div>
          ) : !otpVerified ? (
            <div className="space-y-6 text-center">
              <p className="text-xs text-slate-300 font-medium">Enter the 6-digit verification code</p>
              <div className="flex gap-2 justify-center">
                {otp.map((digit, i) => (
                  <input
                    key={i}
                    id={`otp-input-${i}`}
                    type="tel"
                    maxLength="1"
                    value={digit}
                    onChange={(e) => handleOtpChange(i, e.target.value)}
                    onPaste={handleOtpPaste}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleVerifyOtp();
                      } else if (e.key === 'Backspace' && !digit && i > 0) {
                        document.getElementById(`otp-input-${i - 1}`)?.focus();
                      }
                    }}
                    className="w-10 h-14 text-center text-xl font-bold bg-white/5 border border-white/10 rounded-xl focus:border-[#0ea5a0] outline-none transition-all"
                    autoFocus={i === 0}
                  />
                ))}
              </div>
              <button
                type="button"
                onClick={handleVerifyOtp}
                disabled={isLoading}
                className="w-full py-3 bg-[#0ea5a0] hover:bg-[#0c8e8a] text-white font-bold text-xs rounded-xl transition-all active:scale-95 shadow-md cursor-pointer disabled:opacity-50"
              >
                {isLoading ? 'Verifying...' : 'Authenticate Profile'}
              </button>
              <button
                type="button"
                onClick={() => setOtpSent(false)}
                className="text-xs font-semibold text-slate-400 hover:text-white transition-colors cursor-pointer"
              >
                Resend Code
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex items-center gap-3 p-4 bg-emerald-500/10 rounded-xl">
                <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-white">
                  <CheckCircle2 size={16} />
                </div>
                <span className="text-sm font-semibold text-emerald-400">Security Verified</span>
              </div>
              <form onSubmit={(e) => { e.preventDefault(); handleResetPasswordFinal(); }} className="space-y-4">
                {renderInput("Set New Password", "newPassword", formData.newPassword, "password", false, Lock, false, true)}
                <button
                  type="submit"
                  disabled={isLoading || !formData.newPassword}
                  className="w-full py-3 bg-[#067978] text-white font-bold text-xs rounded-xl hover:bg-[#05605f] transition-all disabled:opacity-50 cursor-pointer"
                >
                  {isLoading ? 'Applying...' : 'Confirm New Password'}
                </button>
              </form>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
};

export default AccountSecuritySection;
